import { LinkText } from '@postscript/components';
import { DYNAMIC_TERMS } from 'components/admin/utils/feature-flags';
import {
  getShopperPrivacyPolicyUrl,
  getShopperTermsUrl,
} from 'components/shopper/utils/helpers';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import React from 'react';

// eslint-disable-next-line no-template-curly-in-string
const TERMS_PLACEHOLDER = '${DISCLAIMER_TERMS_TAG}';
// eslint-disable-next-line no-template-curly-in-string
const PRIVACY_PLACEHOLDER = '${DISCLAIMER_PRIVACY_TAG}';
const TERMS_PLACEHOLDER_ALT = '{terms_of_service}';
const PRIVACY_PLACEHOLDER_ALT = '{privacy_policy}';
export interface DisclaimerDataBackend {
  text: string;
  // eslint-disable-next-line camelcase
  terms_of_service: { text: string; url: string };
  // eslint-disable-next-line camelcase
  privacy_policy: { text: string; url: string };
}
export interface DisclaimerData {
  text: string;
  termsOfService: { text: string; url: string };
  privacyPolicy: { text: string; url: string };
}

export interface DisclaimerTextProps {
  disclaimer: DisclaimerDataBackend;
  shopDomain: string;
  className?: string;
}

/**
 * Renders disclaimer text with clickable links for Terms of Service and Privacy Policy
 *
 * @param disclaimer - The disclaimer data containing text and link information
 * @param shopDomain - The shop domain to construct the legal URLs
 * @returns JSX element with rendered disclaimer text and links
 */
export const DisclaimerText: React.FC<DisclaimerTextProps> = ({
  disclaimer,
  shopDomain,
  className,
}) => {
  const { hasLabsFlag } = usePSLabs();
  const hasDynamicTerms = hasLabsFlag(DYNAMIC_TERMS);

  const termsUrl = getShopperTermsUrl(shopDomain);
  const privacyPolicyUrl = getShopperPrivacyPolicyUrl(shopDomain);

  const parts = disclaimer.text.split(
    /(\$\{DISCLAIMER_TERMS_TAG\}|\$\{DISCLAIMER_PRIVACY_TAG\}|\{terms_of_service\}|\{privacy_policy\})/,
  );

  const content = (
    <>
      {parts.map((part) => {
        if (part === TERMS_PLACEHOLDER || part === TERMS_PLACEHOLDER_ALT) {
          if (hasDynamicTerms) {
            return (
              <React.Fragment key={`disclaimer-terms-text-${part}`}>
                {disclaimer.terms_of_service.text.toUpperCase()}
              </React.Fragment>
            );
          }
          return (
            <LinkText
              key={`disclaimer-terms-link-${part}`}
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {disclaimer.terms_of_service.text.toUpperCase()}
            </LinkText>
          );
        }
        if (part === PRIVACY_PLACEHOLDER || part === PRIVACY_PLACEHOLDER_ALT) {
          if (hasDynamicTerms) {
            return (
              <React.Fragment key={`disclaimer-privacy-text-${part}`}>
                {disclaimer.privacy_policy.text.toUpperCase()}
              </React.Fragment>
            );
          }
          return (
            <LinkText
              key={`disclaimer-privacy-link-${part}`}
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {disclaimer.privacy_policy.text.toUpperCase()}
            </LinkText>
          );
        }
        return (
          <React.Fragment key={`disclaimer-text-${part.substring(0, 10)}`}>
            {part}
          </React.Fragment>
        );
      })}
    </>
  );

  if (className) {
    return <div className={className}>{content}</div>;
  }

  return content;
};

export default DisclaimerText;
